import { DragIndicator } from "@mui/icons-material";
import { Divider, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { useMemo } from "react";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { MeasurementIcon } from "../../../icons/MeasurementIcon";
import { getAssetTypeLabel, UTP2_VARIABLE_CHAR } from "../../../shared/constants/managedSurveyAssetTypes";
import { getTranslatedSubLocationName } from "../../../shared/helpers/SubLocationHelpers";
import { useComponentWidth } from "../../../shared/helpers/useComponentWidth";
import { HvSurveyableAsset } from "../../../shared/interfaces/WorkflowV2";
import type { OrderedMeasurement, WorkflowReducer } from "../../workflowReducer";
import {
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineNumberDot,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineTransparentConnector,
} from "./StyledTimelineComponents";
import { getSubAssetType } from "../../../shared/interfaces/SurveyableAsset";

interface Props {
  workflowReducer: WorkflowReducer;
  measurement: OrderedMeasurement;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  editMode?: boolean;
  isFirstItemInTimeline?: boolean;
}

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    alignItems: "center",
    padding: theme.spacing(1),
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(2),
  },
  marginTopBottom: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  strongText: {
    fontSize: "1.2em",
    fontWeight: 500,
  },
  panelNo: {
    margin: theme.spacing(0, 1),
    fontSize: "1.1em",
  },
  dividerGridItem: {
    flexGrow: 1,
    margin: theme.spacing(0, 1),
  },
  dividerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  divider: {
    flex: "1 1 auto",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
  },
}));

export function MeasurementsOrderedTimelineItem(props: Props) {
  const classes = useStyles();
  const { t } = useTranslation();

  const { containerRef, width } = useComponentWidth<HTMLDivElement>();

  const isSmallComponentWidth = width < 1300;

  const isWorkflowV2 = props.workflowReducer.state.workflow.workflowDetail.version === 2;
  const isGenericWorkflow = props.workflowReducer.state.workflowType === "generic";

  const columns = (() => {
    if (!isGenericWorkflow) {
      return isWorkflowV2 ? (isSmallComponentWidth ? 14 : 12) : 10;
    }
    return isWorkflowV2 ? (isSmallComponentWidth ? 13 : 11) : 8;
  })();

  const measurementText = useMemo(() => {
    if (props.workflowReducer.state.workflow.workflowDetail.version !== 2) {
      return undefined;
    }

    const { parentAssetType: measurementType, parentAssetId } = props.measurement;
    const asset = parentAssetId
      ? (props.workflowReducer.state.workflow.workflowDetail.assets as HvSurveyableAsset[]).find(
          (asset) => asset.assetid === parentAssetId,
        )
      : undefined;

    return (
      asset?.name ??
      (measurementType?.charAt(1) === UTP2_VARIABLE_CHAR
        ? t(`assetTypes.${getAssetTypeLabel(measurementType)}`)
        : t(`assetTypes.${measurementType}`))
    );
  }, [
    props.workflowReducer.state.workflow.workflowDetail.version,
    props.measurement,
    props.workflowReducer.state.workflow.workflowDetail.assets,
    t,
  ]);

  return (
    <TimelineItem ref={containerRef}>
      <TimelineOppositeContent />
      <TimelineSeparator>
        {props.isFirstItemInTimeline ? <TimelineTransparentConnector /> : <TimelineConnector />}
        <TimelineNumberDot variant="filled">{props.measurement.order}</TimelineNumberDot>
        <TimelineConnector />
      </TimelineSeparator>

      <TimelineContent>
        <Grid
          container
          className={classes.gridContainer}
          wrap="nowrap"
          columns={props.editMode ? columns + 1 : columns}
        >
          <Grid xs={1} item>
            <div className={classes.dividerContainer}>
              <Divider variant="fullWidth" className={classes.divider} />
            </div>
          </Grid>

          {isWorkflowV2 && (
            <>
              <Grid item className={classes.gridItem} xs={isSmallComponentWidth ? 3 : 2}>
                <Stack width="100%" direction="row" spacing={2} alignItems="center">
                  <Typography variant="body1" component="h2">
                    {measurementText}
                  </Typography>
                  <Divider variant="fullWidth" className={classes.divider} />
                </Stack>
              </Grid>
            </>
          )}

          <Grid
            item
            className={classes.gridItem}
            xs={isSmallComponentWidth ? 4 : 3}
            style={{ display: "flex", justifyContent: "left" }}
          >
            <Stack direction="row" spacing={2} alignItems="center" width="100%">
              {props.measurement.type && (
                <MeasurementIcon height="1.5em" width="1.5em" measurementType={props.measurement.type} />
              )}
              <span>
                <div className={classes.marginTopBottom}>
                  <Typography variant="body1" component="h2">
                    {t("results.measurementType", {
                      measurementType: `$t(MeasurementTypes.${props.measurement.type} )`,
                    })}
                  </Typography>
                </div>
                {props.measurement.accessory && (
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    component="span"
                    className={classes.marginTopBottom}
                  >
                    {t(`UltrasonicAccessory.${props.measurement.accessory}`)}
                  </Typography>
                )}
              </span>
              <Divider variant="fullWidth" className={classes.divider} />
            </Stack>
          </Grid>

          <Grid item xs={2} className={classes.gridItem}>
            <Stack direction="row" spacing={2} width="100%" alignItems="center">
              <Typography variant="body1" component="span" display="flex" justifySelf="center">
                {props.measurement.access_location
                  ? t(`AccessLocations.${props.measurement.access_location}`)
                  : t("workflows.noAccessLocation")}
              </Typography>
              <Divider variant="fullWidth" className={classes.divider} />
            </Stack>
          </Grid>

          {props.measurement.panelNo && (
            <>
              <Grid item xs={2}>
                <Stack direction="row" spacing={2} width="100%" alignItems="center">
                  <span>
                    <div className={classes.marginTopBottom}>
                      <Typography variant="subtitle2" color="textSecondary" component="span">
                        {`${t("panels.panelNo", { val: getSubAssetType(props.measurement.parentAssetType) })}:`}
                      </Typography>
                      <Typography variant="subtitle2" component="span" className={classes.panelNo}>
                        {props.measurement.panelNo}
                      </Typography>
                    </div>
                    <Typography variant="body2" component="span">
                      {props.measurement.panelName ?? props.measurement.name}
                    </Typography>
                  </span>
                  <Divider variant="fullWidth" className={classes.divider} />
                </Stack>
              </Grid>
            </>
          )}

          <Grid item xs="auto">
            <Stack paddingRight={1}>
              <Typography variant="body1" component="span" className={classes.strongText}>
                {t(`MeasurementComponents.${props.measurement.component}`)}
              </Typography>
              {props.measurement.subloc && (
                <Typography variant="subtitle2" color="textSecondary" component="span" noWrap>
                  {props.measurement.component &&
                    getTranslatedSubLocationName(
                      props.measurement.subloc,
                      props.measurement.component,
                      props.workflowReducer.state.cableNaming,
                    )}
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid xs item>
            <div className={classes.dividerContainer}>
              <Divider variant="fullWidth" className={classes.divider} />
            </div>
          </Grid>

          {props.editMode && (
            <Grid item xs="auto" className={classes.actions}>
              <span {...props.dragHandleProps}>
                <Tooltip title={t<string>("workflows.measurementDragHandle")}>
                  <DragIndicator color="action" />
                </Tooltip>
              </span>
            </Grid>
          )}
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );
}
